import { useCallback, useEffect, useMemo, useState } from "react";

import { Search } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { DataGrid } from "@mui/x-data-grid";
import {
  listConversation,
  listMessagingUser,
} from "@rubi/api-generated/clientRs";
import type { Conversation } from "@rubi/api-generated/clientRs";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface Props {
  orgId: string;
  rubiUserId: string | undefined;
}

const PHONE_SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const INITIAL_PAGE_SIZE = 25;

function formatDate(iso: string, locale?: string): string {
  return new Date(iso).toLocaleDateString(locale, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

function formatTime(iso: string, locale?: string): string {
  return new Date(iso).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatDateTime(iso: string, locale?: string): string {
  return new Date(iso).toLocaleString(locale, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** Last activity proxy: time_closed if set, otherwise time_close_at (the
 * planned auto-close timestamp). The backend does not currently maintain a
 * dedicated "last activity" field on conversation, so this is the best we
 * can do without an extra join against messages. */
function lastUpdatedFor(c: Conversation): string {
  return c.time_closed ?? c.time_close_at;
}

const MessageHistoryPanel = ({ orgId, rubiUserId }: Props) => {
  const { t, i18n } = useTranslation("message");
  const locale = i18n.language;
  const navigate = useNavigate();

  const openConversation = useCallback(
    (phoneNumber: string) => {
      navigate(`/rubimessage/${orgId}/conversation/${phoneNumber}`);
    },
    [navigate, orgId],
  );

  const [phoneSearchInput, setPhoneSearchInput] = useState("");
  const [phoneSearch, setPhoneSearch] = useState("");

  // Debounce phone search to avoid a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(
      () => setPhoneSearch(phoneSearchInput.trim()),
      PHONE_SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [phoneSearchInput]);

  // Reuses the same react-query key as MessagingUsersList so we don't fire a
  // second request just to resolve the selected user's display name.
  const { data: usersData } = useQuery(
    ["messagingUsers", orgId],
    async () => {
      const response = await listMessagingUser({
        query: { rubi_org_id: orgId },
        throwOnError: true,
      });
      return response.data?.messaging_users ?? [];
    },
    { staleTime: 60_000 },
  );

  const selectedUser = rubiUserId
    ? usersData?.find((u) => u.id === rubiUserId)
    : undefined;

  const {
    data: conversationData,
    isFetching,
    isError,
    refetch,
  } = useQuery(
    ["messagingUserConversation", orgId, rubiUserId, phoneSearch],
    async () => {
      if (!rubiUserId) return null;
      const response = await listConversation({
        query: {
          rubi_org_id: orgId,
          participant_rubi_user_id: rubiUserId,
          sms_phone_number: phoneSearch || undefined,
          // Backend caps at 100. One staff user is not expected to participate
          // in more than that; the DataGrid paginates this set client-side.
          limit: 100,
        },
        throwOnError: true,
      });
      return response.data ?? null;
    },
    {
      enabled: Boolean(rubiUserId),
      staleTime: 60_000,
    },
  );

  const columns = useMemo<GridColDef<Conversation>[]>(
    () => [
      {
        field: "citizenName",
        headerName: t("allHistory.columns.citizenName"),
        flex: 1,
        minWidth: 140,
        valueGetter: (_, row) =>
          [row.first_name, row.last_name].filter(Boolean).join(" ").trim() ||
          t("allHistory.unnamedCitizen"),
      },
      {
        field: "sms_phone_number",
        headerName: t("allHistory.columns.phoneNumber"),
        flex: 1,
        minWidth: 140,
      },
      {
        field: "date",
        headerName: t("allHistory.columns.date"),
        flex: 1,
        minWidth: 110,
        valueGetter: (_, row) => formatDate(row.time_created, locale),
      },
      {
        field: "startTime",
        headerName: t("allHistory.columns.startTime"),
        flex: 1,
        minWidth: 110,
        valueGetter: (_, row) => formatTime(row.time_created, locale),
      },
      {
        field: "endTime",
        headerName: t("allHistory.columns.endTime"),
        flex: 1,
        minWidth: 110,
        valueGetter: (_, row) =>
          row.time_closed
            ? formatTime(row.time_closed, locale)
            : t("allHistory.endTimeOngoing"),
      },
      {
        field: "lastUpdated",
        headerName: t("allHistory.columns.lastUpdated"),
        flex: 1.4,
        minWidth: 180,
        valueGetter: (_, row) => formatDateTime(lastUpdatedFor(row), locale),
      },
    ],
    [locale, t],
  );

  if (!rubiUserId) {
    return (
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <Typography color="text.secondary">
          {t("allHistory.selectUserPrompt")}
        </Typography>
      </Paper>
    );
  }

  const userDisplayName = selectedUser
    ? `${selectedUser.first_name} ${selectedUser.last_name}`
    : "";

  const rows = conversationData?.conversations ?? [];

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          bgcolor: "action.hover",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {t("allHistory.historyHeader", { name: userDisplayName })}
        </Typography>
      </Box>

      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          gap: 2,
          alignItems: "center",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <TextField
          size="small"
          placeholder={t("allHistory.searchByPhone")}
          value={phoneSearchInput}
          onChange={(e) => setPhoneSearchInput(e.target.value)}
          sx={{ flex: 1, maxWidth: 480 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          width: 0,
          minWidth: "100%",
        }}
      >
        {isError && (
          <Box sx={{ p: 2 }}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => refetch()}>
                  {t("allHistory.retry")}
                </Button>
              }
            >
              {t("allHistory.loadFailedConversations")}
            </Alert>
          </Box>
        )}
        {!isError && (
          <DataGrid<Conversation>
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            loading={isFetching}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: INITIAL_PAGE_SIZE },
              },
            }}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            rowSelection={false}
            disableColumnFilter
            onRowClick={(params) =>
              openConversation(params.row.sms_phone_number)
            }
            onCellKeyDown={(params, event) => {
              if (event.key === "Enter") {
                event.defaultMuiPrevented = true;
                openConversation(params.row.sms_phone_number);
              }
            }}
            slotProps={{
              loadingOverlay: { variant: "linear-progress" },
            }}
            localeText={{
              noRowsLabel: t("allHistory.noConversations"),
            }}
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: "action.hover",
              },
              "& .MuiDataGrid-row": {
                cursor: "pointer",
              },
            }}
          />
        )}
      </Box>
    </Paper>
  );
};

export default MessageHistoryPanel;
